import {
    CHAT_CHANNEL_LABELS,
    MarketingChatChannelRow,
    MarketingSnapshot,
} from '@/lib/api/marketing/marketing-dashboard-api'
import { MdHelpOutline } from 'react-icons/md'
import DeltaBadge from './delta-badge'
import { deltaOf, formatDuration, formatNumber, formatPercent } from './marketing-format'

interface MarketingAttendanceSectionProps {
    current: MarketingSnapshot
    previous: MarketingSnapshot | null
    onHelp?: () => void
}

const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
// Escala sequencial de uma cor só (azul), do claro ao escuro.
const HEAT_STEPS = ['#e8f1fc', '#c5dcf6', '#94bfee', '#5e9de3', '#2a78d6', '#1c5aa6']

function heatColor(value: number, max: number): string {
    if (!value || !max) return 'transparent'
    const index = Math.min(HEAT_STEPS.length - 1, Math.floor((value / max) * HEAT_STEPS.length))
    return HEAT_STEPS[index]
}

/**
 * Como os contatos foram atendidos: volume por canal, tempo até a primeira resposta e
 * quando eles chegam (mapa de calor dia × hora, em Brasília).
 */
export default function MarketingAttendanceSection({ current, previous, onHelp }: MarketingAttendanceSectionProps) {
    const s = current.summary
    const p = previous?.summary

    const max = Math.max(0, ...current.heatmap.map(h => h.total))
    const cell = (dow: number, hour: number) =>
        current.heatmap.find(h => h.dow === dow && h.hour === hour)?.total ?? 0

    const prevChannel = (channel: string): MarketingChatChannelRow | undefined =>
        previous?.chatChannels.find(c => c.channel === channel)

    const stats: Array<{
        label: string
        value: string
        delta: ReturnType<typeof deltaOf>
        hint?: string
    }> = [
        {
            label: 'Conversas novas',
            value: formatNumber(s.newChats),
            delta: p ? deltaOf(s.newChats, p.newChats) : null,
            hint: 'Contato que nunca tinha conversado',
        },
        {
            label: 'Contatos que escreveram',
            value: formatNumber(s.contacts),
            delta: p ? deltaOf(s.contacts, p.contacts) : null,
            hint: 'Novos e recorrentes',
        },
        {
            label: 'Primeira resposta (mediana)',
            value: formatDuration(s.medianFirstResponseSeconds),
            delta: p ? deltaOf(s.medianFirstResponseSeconds, p.medianFirstResponseSeconds, 'lower-better') : null,
        },
        {
            label: 'Respondidos em até 5 min',
            value: formatPercent(s.answeredWithin5mRate),
            delta: p ? deltaOf(s.answeredWithin5mRate, p.answeredWithin5mRate) : null,
        },
        {
            label: 'Sem resposta',
            value: formatNumber(s.unanswered),
            delta: p ? deltaOf(s.unanswered, p.unanswered, 'lower-better') : null,
        },
        {
            label: 'Primeira resposta pela IA',
            value: formatPercent(s.aiAnsweredRate),
            delta: p ? deltaOf(s.aiAnsweredRate, p.aiAnsweredRate, 'neutral') : null,
        },
    ]

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
            <div className="flex items-start justify-between gap-2 mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-base-content">Atendimento dos contatos</h3>
                    <p className="text-sm text-base-content/60">WhatsApp, Instagram e Messenger</p>
                </div>
                {onHelp && (
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-circle text-base-content/40 hover:text-base-content"
                        onClick={onHelp}
                        aria-label="Como o atendimento é medido?"
                        title="Como o atendimento é medido?"
                    >
                        <MdHelpOutline className="text-base" />
                    </button>
                )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
                {stats.map(stat => (
                    <div key={stat.label} className="rounded-lg bg-base-200/50 p-3">
                        <p className="text-[10px] font-medium uppercase tracking-wide text-base-content/50">
                            {stat.label}
                        </p>
                        <div className="mt-1 flex items-center gap-2">
                            <span className="text-lg font-bold">{stat.value}</span>
                            <DeltaBadge delta={stat.delta} />
                        </div>
                        {stat.hint && <p className="text-[11px] text-base-content/50">{stat.hint}</p>}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="overflow-x-auto">
                    <h4 className="text-sm font-semibold mb-2">Por canal</h4>
                    {current.chatChannels.length === 0 ? (
                        <p className="text-sm text-base-content/50 py-6 text-center">Sem conversas no período</p>
                    ) : (
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>Canal</th>
                                    <th className="text-right">Novas</th>
                                    <th className="text-right">Contatos</th>
                                    <th className="text-right">1ª resposta</th>
                                    <th className="text-right">Humano</th>
                                    <th className="text-right">IA</th>
                                </tr>
                            </thead>
                            <tbody>
                                {current.chatChannels.map(row => {
                                    const prev = prevChannel(row.channel)
                                    return (
                                        <tr key={row.channel}>
                                            <td className="font-medium">{CHAT_CHANNEL_LABELS[row.channel] ?? row.channel}</td>
                                            <td className="text-right">
                                                <div className="flex items-center justify-end gap-1.5">
                                                    {formatNumber(row.newChats)}
                                                    {previous && <DeltaBadge delta={deltaOf(row.newChats, prev?.newChats ?? 0)} />}
                                                </div>
                                            </td>
                                            <td className="text-right">{formatNumber(row.contacts)}</td>
                                            <td className="text-right whitespace-nowrap">
                                                {formatDuration(row.medianFirstResponseSeconds)}
                                            </td>
                                            <td className="text-right whitespace-nowrap">{formatDuration(row.medianHumanSeconds)}</td>
                                            <td className="text-right whitespace-nowrap">{formatDuration(row.medianAiSeconds)}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    )}
                    <p className="mt-2 text-[11px] text-base-content/50">
                        Tempos em mediana: metade dos contatos foi respondida antes disso.
                    </p>
                </div>

                <div className="overflow-x-auto">
                    <h4 className="text-sm font-semibold mb-2">Quando os contatos chegam</h4>
                    {max === 0 ? (
                        <p className="text-sm text-base-content/50 py-6 text-center">Sem contatos no período</p>
                    ) : (
                        <div className="min-w-[560px]">
                            <div className="grid gap-[2px]" style={{ gridTemplateColumns: '32px repeat(24, minmax(0, 1fr))' }}>
                                <span />
                                {Array.from({ length: 24 }, (_, h) => (
                                    <span key={h} className="text-[9px] text-center text-base-content/50">
                                        {h % 3 === 0 ? `${h}h` : ''}
                                    </span>
                                ))}
                                {DAYS.map((day, dow) => (
                                    <div key={day} className="contents">
                                        <span className="text-[10px] text-base-content/60 self-center">{day}</span>
                                        {Array.from({ length: 24 }, (_, hour) => {
                                            const value = cell(dow, hour)
                                            return (
                                                <div
                                                    key={hour}
                                                    className="h-5 rounded-sm border border-base-200"
                                                    style={{ backgroundColor: heatColor(value, max) }}
                                                    title={`${day}, ${hour}h: ${formatNumber(value)} contato(s)`}
                                                />
                                            )
                                        })}
                                    </div>
                                ))}
                            </div>
                            <div className="mt-2 flex items-center justify-end gap-1 text-[10px] text-base-content/50">
                                Menos
                                {HEAT_STEPS.map(color => (
                                    <span key={color} className="inline-block h-3 w-4 rounded-sm" style={{ backgroundColor: color }} />
                                ))}
                                Mais
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
